"use client";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CreativeCard,
  CreativesGridSkeleton,
  CreateAdWithIAModal,
  ViewCreativeModal,
} from "@/feature/ads/cretives";
import { creativesService } from "@/feature/ads/cretives/services/creatives.service";
import type { CreativeItem, ProductInfoCreative } from "@/feature/ads/cretives/types/creatives.types";
import { ProductFilterDropdown } from "@/feature/ads/cretives/components/ProductFilterDropdown";
import { AngleFilterDropdown } from "@/feature/ads/cretives/components/AngleFilterDropdown";
import { useAuth } from "@/context/AuthContext";
import { dictionary, Language } from "@/utils/dictionary";
import AdsSpyView from "@/feature/ads/spy/components/AdsSpyView";
import type { AdsSpyViewMode } from "@/feature/ads/spy/components/AdsSpyView";
import HyphensTabContent from "@/components/narratives/HyphensTabContent";
import useCreativeGenerationStore, { type GeneratingItem } from "@/store/creativeGenerationStore";
import useVideoGenerationStore from "@/store/videoGenerationStore";
import { useVideoInFlightStore } from "@/store/videoInFlightStore";
import { useVideoDraftStore } from "@/store/videoDraftStore";
import useAdsPageStore from "@/store/adsPageStore";
import { listVideos } from "@/services/ads/videos/videosService";
import type { VideoStatusResponse } from "@/services/ads/videos/videos.types";
import {
  getVideoDraft,
  getVideoDraftRenderPayloadPreview,
} from "@/services/ads/videos/videoDraftService";
import type {
  VideoDraftRenderPayloadPreview,
  VideoDraftResponse,
} from "@/services/ads/videos/videoDraft.types";
import { GeneratingPlaceholderCard } from "@/feature/ads/cretives/components/GeneratingPlaceholderCard";
import { CreateVideoModal } from "@/feature/videos/components/CreateVideoModal";
import { VideoEditorPanel } from "@/feature/videos/components/VideoEditorPanel";
import { Plus, Video, X, Eye, ChevronLeft, ChevronRight, ChevronDown, Download, Sparkles, RefreshCw, Share2, Search, Star, Info, Copy, CheckCircle2, Loader2, Scissors, Pencil } from "lucide-react";
import { Chip } from "@/components/common/chip/Chip";
import { ButtonFluxi } from "@/components/common/Buttons/ButtonFluxi/ButtonFluxi";
import { membershipAxios, membershipStatsAxios } from "@/services/membership/MembershipAxios";
import { ModalWrapper } from "@/components/modals/ModalWrapper";
import { BuyCreditsModal } from "@/components/modals/BuyCreditsModal";
import { handleApi } from "@/utils/handleApi";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import CampaignsView from "@/components/campaigns/CampaignsView";
import { useMetaCampaignsFlag } from "@/hooks/useMetaCampaignsFlag";
import { DateFilter } from "@/components/sales/DateFilter";
import { getMetaIntegration, getMetaAdAccounts, updateMetaAdAccount, type MetaAdAccount } from "@/services/meta/metaService";
import { shareVideo } from "@/feature/videos/utils/shareVideo";

type AdsPageTab = "images" | "scripts" | "video-studio" | "spy" | "campaigns";
type VideoCreativeItem = CreativeItem & { reference_id?: string };

const VIDEO_GALLERY_PAGE_SIZE = 12;

function copyTextWithSelection(text: string) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  textarea.style.left = "0";
  textarea.style.top = "0";
  textarea.style.width = "1px";
  textarea.style.height = "1px";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  return copied;
}

async function copyTextToClipboard(text: string) {
  if (copyTextWithSelection(text)) return;
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  throw new Error("Copy command failed");
}

function recordValue(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function numberValue(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function recordArrayValue(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.map(recordValue).filter((item): item is Record<string, unknown> => !!item)
    : [];
}

function stringArrayValue(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && !!item.trim())
    : [];
}

function getDraftPlaceholderReferenceId(item: GeneratingItem) {
  return item.id
    .replace(/^draft-active-/, "")
    .replace(/^draft-in-flight-/, "")
    .replace(/^draft-settling-/, "");
}

function VideoGallerySkeleton({ count = 6 }: { count?: number }) {
  return (
    <div
      className="grid gap-6 mt-4"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}
      aria-busy="true"
    >
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          className="overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-sm"
        >
          <div className="relative aspect-square overflow-hidden bg-slate-100">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-100 via-white to-slate-200" />
            <div className="absolute inset-y-0 -left-1/2 w-1/2 animate-shine skew-x-[-18deg] bg-white/70 blur-xl" />
            <div className="absolute right-2.5 top-2.5 h-5 w-16 rounded-full bg-white/70" />
          </div>
          <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2.5">
            <div className="h-5 w-5 flex-shrink-0 rounded-full bg-slate-100" />
            <div className="h-3.5 w-32 rounded-full bg-slate-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

const COPY_NAME_SUFFIX_REGEX = /\s+(Copia\s+\d+)\s*$/i;

function splitCopyNameSuffix(name?: string | null) {
  const displayName = name?.trim() || "Video";
  const match = displayName.match(COPY_NAME_SUFFIX_REGEX);
  if (!match) {
    return { baseName: displayName, copySuffix: null };
  }

  return {
    baseName: displayName.replace(COPY_NAME_SUFFIX_REGEX, "").trim() || displayName,
    copySuffix: match[1],
  };
}

function VideoPreviewCard({ item, onView }: { item: CreativeItem; onView: () => void }) {
  const { baseName, copySuffix } = splitCopyNameSuffix(item.product_name);
  const displayName = item.product_name || "Video";

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.video_url) return;
    window.open(item.video_url, "_blank");
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    shareVideo({ url: item.video_url, title: item.product_name });
  };

  return (
    <div
      className="relative overflow-hidden rounded-xl bg-card border border-border/40 group cursor-pointer"
      onClick={onView}
    >
      <div className="relative w-full aspect-square overflow-hidden bg-gradient-to-b from-[#F8F8FF] to-white">
        {/* Thumbnail */}
        {item.base_image_url ? (
          <img
            src={item.base_image_url}
            alt={item.product_name || "Video"}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Video className="h-10 w-10 text-indigo-300" />
          </div>
        )}

        {/* Hover overlay with actions */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-150 flex items-center justify-center">
          <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
            <button
              onClick={(e) => { e.stopPropagation(); onView(); }}
              className="h-8 w-8 rounded-full flex items-center justify-center shadow-sm transition-colors bg-background/90 text-foreground hover:bg-background"
              title="Ver"
            >
              <Eye className="h-4 w-4" />
            </button>
            {item.video_url && (
              <>
                <button
                  onClick={handleShare}
                  className="h-8 w-8 rounded-full flex items-center justify-center shadow-sm transition-colors bg-background/90 text-foreground hover:bg-background"
                  title="Compartir"
                >
                  <Share2 className="h-4 w-4" />
                </button>
                <button
                  onClick={handleDownload}
                  className="h-8 w-8 rounded-full flex items-center justify-center shadow-sm transition-colors bg-background/90 text-foreground hover:bg-background"
                  title="Descargar"
                >
                  <Download className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Badge */}
        <div className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-indigo-500 text-white px-2 py-0.5 rounded-full text-[10px] font-semibold shadow-sm">
          <Video className="h-3 w-3" />
          Video
        </div>
      </div>

      <div className="px-3 py-2.5 flex items-center gap-2 border-t border-border/30">
        {item.product_image_url && (
          <div className="h-5 w-5 rounded-full overflow-hidden flex-shrink-0 bg-muted/50 border border-border/30">
            <img src={item.product_image_url} alt="" className="h-full w-full object-cover" />
          </div>
        )}
        <span className="min-w-0 flex-1 truncate text-sm font-medium leading-tight text-foreground" title={displayName}>
          {baseName}
        </span>
        {copySuffix && (
          <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-slate-600">
            {copySuffix}
          </span>
        )}
      </div>
    </div>
  );
}

function ViewVideoModal({
  videos,
  initialIndex,
  open,
  onClose,
  onVideoEdited,
}: {
  videos: VideoCreativeItem[];
  initialIndex: number;
  open: boolean;
  onClose: () => void;
  onVideoEdited?: (targetReferenceId?: string | null) => void;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [contextOpen, setContextOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [contextDraft, setContextDraft] = useState<VideoDraftResponse | null>(null);
  const [contextRenderPayload, setContextRenderPayload] = useState<VideoDraftRenderPayloadPreview | null>(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);
  const [contextRenderError, setContextRenderError] = useState<string | null>(null);
  const [copiedContext, setCopiedContext] = useState(false);
  const [pendingEditedReferenceId, setPendingEditedReferenceId] = useState<string | null>(null);
  const [videoTitleOverrides, setVideoTitleOverrides] = useState<Record<string, string>>({});
  const [editingVideoTitle, setEditingVideoTitle] = useState(false);
  const [draftVideoTitle, setDraftVideoTitle] = useState("");
  const videoTitleInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open && initialIndex >= 0) setCurrentIndex(initialIndex);
  }, [open, initialIndex]);

  const safeIndex = videos.length > 0 ? Math.max(0, Math.min(currentIndex, videos.length - 1)) : 0;
  const current = videos[safeIndex] ?? null;
  const currentReferenceId = current?.reference_id ?? null;
  const videoTitleKey = currentReferenceId ?? (current?.id != null ? `creative-${current.id}` : `video-${safeIndex}`);
  const fallbackVideoTitle = stringValue(current?.name) ?? stringValue(current?.product_name) ?? "Video";
  const editorVideoTitle = videoTitleOverrides[videoTitleKey] || fallbackVideoTitle;

  useEffect(() => {
    if (!open) {
      setContextOpen(false);
      setEditorOpen(false);
    }
    setContextDraft(null);
    setContextRenderPayload(null);
    setContextError(null);
    setContextRenderError(null);
    setCopiedContext(false);
    setContextLoading(false);
    if (!open) setPendingEditedReferenceId(null);
  }, [open, currentReferenceId]);

  useEffect(() => {
    if (!editingVideoTitle) return;
    videoTitleInputRef.current?.focus();
    videoTitleInputRef.current?.select();
  }, [editingVideoTitle]);

  useEffect(() => {
    setEditingVideoTitle(false);
    setDraftVideoTitle("");
  }, [videoTitleKey]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (editingVideoTitle) return;
      if (contextOpen || editorOpen) {
        if (e.key === "Escape" || e.key === "ArrowLeft") {
          setContextOpen(false);
          setEditorOpen(false);
        }
        return;
      }
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && currentIndex > 0) setCurrentIndex((i) => i - 1);
      if (e.key === "ArrowRight" && currentIndex < videos.length - 1) setCurrentIndex((i) => i + 1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose, currentIndex, videos.length, contextOpen, editorOpen, editingVideoTitle]);

  useEffect(() => {
    if (!pendingEditedReferenceId) return;
    const nextIndex = videos.findIndex((video) => video.reference_id === pendingEditedReferenceId);
    if (nextIndex < 0) return;
    setCurrentIndex(nextIndex);
    setContextOpen(false);
    setEditorOpen(false);
    setPendingEditedReferenceId(null);
  }, [pendingEditedReferenceId, videos]);

  if (!open || videos.length === 0 || !current) return null;

  const hasPrevious = safeIndex > 0;
  const hasNext = safeIndex < videos.length - 1;
  const internalScreenOpen = contextOpen || editorOpen;
  const contextPayload = currentReferenceId
    ? {
      reference_id: currentReferenceId,
      video: {
        url: current.video_url ?? null,
        thumbnail_url: current.base_image_url ?? null,
        product_id: current.product_id ?? null,
        product_name: current.product_name ?? null,
        product_image_url: current.product_image_url ?? null,
        format: current.format ?? "9:16",
      },
      draft: contextDraft
        ? {
          status: contextDraft.status,
          style_id: contextDraft.style_id,
          duration: contextDraft.duration,
          language: contextDraft.language,
          credits_charged: contextDraft.credits_charged,
          base_image_url: contextDraft.base_image_url,
          end_image_url: contextDraft.end_image_url,
          reference_images: contextDraft.reference_images,
          audio_mode: contextDraft.audio_mode,
          voice_config: contextDraft.voice_config,
          created_at: contextDraft.created_at,
          updated_at: contextDraft.updated_at,
          approved_at: contextDraft.approved_at,
        }
        : null,
      creation_inputs: contextDraft?.creation_inputs ?? null,
      director_payload: contextDraft?.director_payload ?? null,
      render_payload_preview: contextRenderPayload,
      render_payload_preview_error: contextRenderError,
    }
    : null;
  const contextPayloadJson = contextPayload ? JSON.stringify(contextPayload, null, 2) : "";
  const contextCreationInputs = recordValue(contextDraft?.creation_inputs) ?? {};
  const contextDirectorPayload = recordValue(contextDraft?.director_payload) ?? {};
  const contextAvatarConfig = recordValue(contextCreationInputs.avatar_config) ?? {};
  const contextVoiceConfig =
    recordValue(contextDraft?.voice_config) ?? recordValue(contextCreationInputs.voice_config) ?? {};
  const contextRenderReferences = recordValue(contextDirectorPayload.render_references) ?? {};
  const contextClips = recordArrayValue(contextDirectorPayload.clips);
  const contextBaseImages = recordArrayValue(contextDirectorPayload.video_base_images);
  const contextReferenceImages = stringArrayValue(contextDraft?.reference_images);
  const contextAngleName = stringValue(contextCreationInputs.sale_angle_name);
  const contextAngleDescription = stringValue(contextCreationInputs.sale_angle_description);
  const contextFullVoiceover = stringValue(contextDirectorPayload.full_voiceover);
  const contextConceptName = stringValue(contextDirectorPayload.concept_name);
  const contextMusic = recordValue(contextDirectorPayload.music) ?? {};
  const contextAvatarPreviewUrl =
    stringValue(contextAvatarConfig.recommendation_preview_url) ??
    stringValue(contextCreationInputs.pre_generated_base_image_url) ??
    stringValue(contextRenderReferences.selected_avatar_image_url);
  const contextProductImageUrl =
    stringValue(contextRenderReferences.product_image_url) ??
    current.product_image_url ??
    current.image_url ??
    null;
  const contextImageCards = [
    ...(contextAvatarPreviewUrl
      ? [{
        label: "Avatar seleccionado",
        subtitle: stringValue(contextAvatarConfig.recommendation_label) ?? "Referencia visual",
        url: contextAvatarPreviewUrl,
      }]
      : []),
    ...(contextProductImageUrl
      ? [{
        label: "Producto",
        subtitle: "Referencia visual",
        url: contextProductImageUrl,
      }]
      : []),
    ...contextReferenceImages.map((url, index) => {
      const imageSpec = contextBaseImages[index];
      const imageKey = stringValue(imageSpec?.image_key) ?? `@Image${index + 3}`;
      const startFrame = imageSpec?.start_image_usage === true;
      return {
        label: `${imageKey}${startFrame ? " · Start frame" : ""}`,
        subtitle: stringValue(imageSpec?.prompt) ?? "Imagen generada",
        url,
      };
    }),
  ];

  const handleDownload = () => {
    if (!current.video_url) return;
    window.open(current.video_url, "_blank");
  };

  const handleShare = () => {
    shareVideo({ url: current.video_url, title: current.product_name });
  };

  const handleOpenContext = async () => {
    if (!currentReferenceId) return;
    setEditorOpen(false);
    setContextOpen(true);
    if (contextDraft?.reference_id === currentReferenceId) return;

    setContextLoading(true);
    setContextError(null);
    setContextRenderError(null);

    const [draftResult, renderResult] = await Promise.all([
      getVideoDraft(currentReferenceId),
      getVideoDraftRenderPayloadPreview(currentReferenceId),
    ]);

    if (draftResult.res) {
      setContextDraft(draftResult.res);
    } else {
      setContextDraft(null);
      setContextError(draftResult.error?.message || "No pudimos cargar el contexto del video.");
    }

    if (renderResult.res) {
      setContextRenderPayload(renderResult.res);
    } else {
      setContextRenderPayload(null);
      setContextRenderError(renderResult.error?.message || "No pudimos cargar el payload de render.");
    }

    setContextLoading(false);
  };

  const handleOpenEditor = () => {
    if (!currentReferenceId || !current.video_url) return;
    setContextOpen(false);
    setEditorOpen(true);
  };

  const startVideoTitleEdit = () => {
    setDraftVideoTitle(editorVideoTitle);
    setEditingVideoTitle(true);
  };

  const commitVideoTitleEdit = () => {
    const nextTitle = draftVideoTitle.trim();
    if (nextTitle) {
      setVideoTitleOverrides((prev) => ({ ...prev, [videoTitleKey]: nextTitle }));
    }
    setDraftVideoTitle("");
    setEditingVideoTitle(false);
  };

  const cancelVideoTitleEdit = () => {
    setDraftVideoTitle("");
    setEditingVideoTitle(false);
  };

  const handleOpenEditedVideo = (targetReferenceId?: string | null) => {
    if (!targetReferenceId) return;
    const nextIndex = videos.findIndex((video) => video.reference_id === targetReferenceId);
    if (nextIndex >= 0) {
      setCurrentIndex(nextIndex);
      setContextOpen(false);
      setEditorOpen(false);
      return;
    }
    setPendingEditedReferenceId(targetReferenceId);
    onVideoEdited?.(targetReferenceId);
  };

  const handleCopyContext = async () => {
    if (!contextPayloadJson) return;
    try {
      await copyTextToClipboard(contextPayloadJson);
      setCopiedContext(true);
      window.setTimeout(() => setCopiedContext(false), 1400);
    } catch {
      setContextError("No pudimos copiar el JSON.");
    }
  };

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center transition-colors duration-200",
        internalScreenOpen ? "bg-white/90 backdrop-blur-md" : "bg-black/80 backdrop-blur-sm"
      )}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className={cn(
          "relative mx-4 flex w-full flex-col overflow-hidden rounded-2xl border shadow-2xl",
          editorOpen
            ? "h-[min(98vh,1040px)] max-w-[1360px] border-slate-200/70 bg-white"
            : contextOpen
            ? "h-[min(92vh,780px)] max-w-[1180px] border-slate-200/70 bg-white"
            : "max-h-[95vh] max-w-4xl border-border/30 bg-background"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {internalScreenOpen ? (
          <div className="flex items-center justify-between gap-4 border-b border-slate-200/70 bg-slate-50/80 px-6 py-4 flex-shrink-0">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {contextOpen && (
                <button
                  type="button"
                  onClick={() => setContextOpen(false)}
                  aria-label="Volver a preview"
                  className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-100 hover:text-slate-950"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  <span>Volver</span>
                </button>
              )}
              {editorOpen ? (
                <div className="group/title flex min-w-0 flex-1 items-center gap-1.5">
                {editingVideoTitle ? (
                  <input
                    ref={videoTitleInputRef}
                    value={draftVideoTitle}
                    onChange={(event) => setDraftVideoTitle(event.target.value)}
                    onBlur={commitVideoTitleEdit}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        commitVideoTitleEdit();
                      }
                      if (event.key === "Escape") {
                        event.preventDefault();
                        cancelVideoTitleEdit();
                      }
                    }}
                    aria-label="Título del video"
                    className="min-w-0 max-w-[520px] flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-left text-base font-semibold leading-6 text-slate-950 outline-none transition focus:border-gray-500 focus:ring-2 focus:ring-gray-100"
                  />
                ) : (
                  <>
                    <h3 className="min-w-0 truncate text-base font-semibold text-slate-950">
                      {editorVideoTitle}
                    </h3>
                    <button
                      type="button"
                      onClick={startVideoTitleEdit}
                      aria-label="Editar título del video"
                      title="Editar título"
                      className="rounded-full p-1 text-gray-400 opacity-0 transition hover:bg-gray-100 hover:text-gray-700 focus:bg-gray-100 focus:text-gray-700 focus:opacity-100 group-hover/title:opacity-100"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
                </div>
              ) : (
                <div className="min-w-0">
                  <h3 className="truncate text-base font-semibold text-slate-950">
                    Contexto del video creado
                  </h3>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-950"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 p-4 border-b border-border/40 flex-shrink-0">
            <span className="text-sm font-medium text-muted-foreground">
              {safeIndex + 1} de {videos.length}
            </span>
            <div className="flex items-center gap-1.5">
              {current.reference_id && (
                <button
                  type="button"
                  onClick={handleOpenContext}
                  disabled={contextLoading}
                  aria-label="Ver contexto"
                  className={cn(
                    "flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-muted-foreground transition-colors",
                    "hover:bg-muted/60 hover:text-foreground",
                    "disabled:cursor-not-allowed disabled:opacity-50"
                  )}
                >
                  {contextLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Info className="h-3.5 w-3.5" />}
                  <span className="hidden sm:inline">Ver contexto</span>
                </button>
              )}
              <button
                type="button"
                onClick={handleShare}
                disabled={!current.video_url}
                aria-label="Compartir"
                className={cn(
                  "flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-muted-foreground transition-colors",
                  "hover:bg-muted/60 hover:text-foreground",
                  "disabled:cursor-not-allowed disabled:opacity-50"
                )}
              >
                <Share2 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Compartir</span>
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {!internalScreenOpen && (
          <>
        {/* Video area with navigation */}
        <div className="relative flex-1 flex items-center justify-center p-4 sm:p-6 min-h-0 overflow-hidden bg-gradient-to-b from-muted/30 to-muted/10">
          {/* Navigation arrows */}
          {videos.length > 1 && (
            <>
              <button
                onClick={() => hasPrevious && setCurrentIndex((i) => i - 1)}
                disabled={!hasPrevious}
                className={cn(
                  "absolute left-4 sm:left-6 z-10 p-3 rounded-full transition-all duration-200 shadow-sm",
                  "bg-background/80 backdrop-blur-sm border border-border/50 hover:bg-background text-foreground",
                  "disabled:opacity-30 disabled:pointer-events-none",
                  hasPrevious && "hover:scale-105"
                )}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => hasNext && setCurrentIndex((i) => i + 1)}
                disabled={!hasNext}
                className={cn(
                  "absolute right-4 sm:right-6 z-10 p-3 rounded-full transition-all duration-200 shadow-sm",
                  "bg-background/80 backdrop-blur-sm border border-border/50 hover:bg-background text-foreground",
                  "disabled:opacity-30 disabled:pointer-events-none",
                  hasNext && "hover:scale-105"
                )}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}

          {/* Video or Image fallback */}
          <div className="relative flex items-center justify-center h-full w-full min-h-0">
            {current.video_url ? (
              <video
                key={current.id}
                src={current.video_url}
                poster={current.base_image_url || undefined}
                controls
                autoPlay
                className="max-h-[65vh] w-auto rounded-lg shadow-2xl object-contain"
                style={{ aspectRatio: "9/16" }}
              />
            ) : current.base_image_url ? (
              <img
                src={current.base_image_url}
                alt={current.product_name || "Video"}
                className="max-h-[65vh] w-auto rounded-lg shadow-2xl object-contain"
                style={{ aspectRatio: "9/16" }}
              />
            ) : (
              <div className="flex items-center justify-center h-[65vh] rounded-lg bg-muted/30" style={{ aspectRatio: "9/16" }}>
                <Video className="h-16 w-16 text-muted-foreground/30" />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border/40 bg-background flex-shrink-0">
          <div className="flex flex-col gap-3 px-4 py-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-foreground truncate">
                  {current.product_name || "Video"}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Video · 9:16
                </p>
              </div>
              <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2">
              {current.reference_id && current.video_url && (
                <button
                  type="button"
                  onClick={handleOpenEditor}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors",
                    "border border-primary/25 bg-primary/10 text-primary hover:bg-primary/15"
                  )}
                >
                  <Scissors className="h-4 w-4" />
                  Editar
                </button>
              )}
              <button
                type="button"
                onClick={handleDownload}
                disabled={!current.video_url}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-colors",
                  "shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/25",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
                style={{ background: "linear-gradient(to right, #4368E5, #4368E5)" }}
              >
                <Download className="h-4 w-4" />
                Descargar
              </button>
              </div>
            </div>

            {videos.length > 1 && (
              <div className="w-full overflow-x-auto py-1 px-0.5">
                <div className="flex min-w-max gap-2.5">
                  {videos.map((v, index) => (
                    <motion.button
                      key={v.id ?? index}
                      type="button"
                      onClick={() => setCurrentIndex(index)}
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.96 }}
                      className={cn(
                        "h-[68px] w-[68px] rounded-md overflow-hidden flex-shrink-0 transition-all duration-200 bg-muted",
                        index === safeIndex
                          ? "ring-2 ring-primary ring-offset-1 ring-offset-background shadow-lg"
                          : "opacity-55 hover:opacity-100 border border-border/50"
                      )}
                    >
                      {v.base_image_url ? (
                        <img src={v.base_image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
          </>
        )}

        {editorOpen && currentReferenceId && (
          <VideoEditorPanel
            referenceId={currentReferenceId}
            videoUrl={current.video_url}
            posterUrl={current.base_image_url}
            productName={current.product_name}
            onOpenFinished={handleOpenEditedVideo}
          />
        )}

        {contextOpen && (
          <>
              {contextLoading && !contextDraft ? (
                <div className="flex min-h-[520px] items-center justify-center bg-slate-50 text-sm text-slate-500">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin text-primary" />
                  Cargando contexto del video...
                </div>
              ) : (
                <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50">
                  <div className="grid gap-5 p-5 lg:grid-cols-[0.36fr_0.64fr]">
                    <aside className="min-w-0 space-y-4">
                      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="flex gap-3">
                          {contextProductImageUrl ? (
                            <img
                              src={contextProductImageUrl}
                              alt=""
                              className="h-16 w-16 shrink-0 rounded-lg border border-slate-200 object-cover"
                            />
                          ) : (
                            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-slate-100">
                              <Video className="h-5 w-5 text-slate-400" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Producto</p>
                            <p className="mt-1 text-sm font-semibold leading-snug text-slate-950">
                              {current.product_name || contextDraft?.product_name || "Video"}
                            </p>
                            {contextConceptName && (
                              <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500">{contextConceptName}</p>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-2">
                          {[
                            { label: "Estado", value: contextDraft?.status || "Sin dato" },
                            { label: "Duración", value: contextDraft?.duration ? `${contextDraft.duration}s` : "Sin dato" },
                            { label: "Estilo", value: contextDraft?.style_id || "Sin dato" },
                            { label: "Audio", value: contextDraft?.audio_mode || "Sin dato" },
                          ].map((item) => (
                            <div key={item.label} className="rounded-lg bg-slate-50 px-3 py-2">
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{item.label}</p>
                              <p className="mt-1 truncate text-xs font-semibold text-slate-900">{item.value}</p>
                            </div>
                          ))}
                        </div>
                      </section>

                      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Ángulo creativo</p>
                        <h4 className="mt-1 text-sm font-semibold text-slate-950">
                          {contextAngleName || "Sin ángulo guardado"}
                        </h4>
                        <p className="mt-2 text-xs leading-relaxed text-slate-600">
                          {contextAngleDescription || "No hay descripción de ángulo en los insumos del draft."}
                        </p>
                      </section>

                      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Avatar</p>
                        <div className="mt-3 flex gap-3">
                          {contextAvatarPreviewUrl && (
                            <img
                              src={contextAvatarPreviewUrl}
                              alt=""
                              className="h-20 w-16 shrink-0 rounded-lg border border-slate-200 object-cover"
                            />
                          )}
                          <div className="min-w-0 text-xs leading-relaxed text-slate-600">
                            <p className="font-semibold text-slate-950">
                              {stringValue(contextAvatarConfig.recommendation_label) || "Referencia visual"}
                            </p>
                            <p>Fuente: {stringValue(contextAvatarConfig.source) || "Sin dato"}</p>
                            {stringValue(contextAvatarConfig.used_reference_id) && (
                              <p className="mt-1 break-all text-[11px] text-slate-500">
                                Video usado: {stringValue(contextAvatarConfig.used_reference_id)}
                              </p>
                            )}
                          </div>
                        </div>
                      </section>

                      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Voz y música</p>
                        <div className="mt-3 space-y-2 text-xs leading-relaxed text-slate-600">
                          <p>
                            <span className="font-semibold text-slate-900">Voz:</span>{" "}
                            {stringValue(contextVoiceConfig.voice_name) ||
                              stringValue(contextVoiceConfig.voice_id) ||
                              "Sin dato"}
                          </p>
                          <p>
                            <span className="font-semibold text-slate-900">Proveedor:</span>{" "}
                            {stringValue(contextVoiceConfig.provider) || "Sin dato"}
                          </p>
                          <p>
                            <span className="font-semibold text-slate-900">Acento:</span>{" "}
                            {stringValue(contextVoiceConfig.accent_city) || "Sin dato"}
                          </p>
                          {(stringValue(contextVoiceConfig.music_prompt) || stringValue(contextMusic.prompt)) && (
                            <p>
                              <span className="font-semibold text-slate-900">Música:</span>{" "}
                              {stringValue(contextVoiceConfig.music_prompt) || stringValue(contextMusic.prompt)}
                            </p>
                          )}
                        </div>
                      </section>

                      {(contextError || contextRenderError) && (
                        <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs leading-relaxed text-amber-900">
                          {contextError || contextRenderError}
                        </section>
                      )}
                    </aside>

                    <main className="min-w-0 space-y-4">
                      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-primary" />
                          <h4 className="text-sm font-semibold text-slate-950">Guion final</h4>
                        </div>
                        <p className="mt-3 whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm leading-relaxed text-slate-700">
                          {contextFullVoiceover || "No hay guion final guardado en el director payload."}
                        </p>
                      </section>

                      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="flex items-center justify-between gap-3">
                          <h4 className="text-sm font-semibold text-slate-950">Estructura de clips</h4>
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                            {contextClips.length || 0} clips
                          </span>
                        </div>
                        <div className="mt-3 grid gap-3">
                          {contextClips.length > 0 ? contextClips.map((clip, index) => {
                            const beats = recordArrayValue(clip.beats);
                            const clipKey = stringValue(clip.clip_key) ?? `clip_${index + 1}`;
                            const startImageKey = stringValue(clip.start_image_key);
                            const duration = numberValue(clip.duration_seconds);
                            return (
                              <div key={`${clipKey}-${index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-900 shadow-sm">
                                    {clipKey}
                                  </span>
                                  {duration && (
                                    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-600 shadow-sm">
                                      {duration}s
                                    </span>
                                  )}
                                  {startImageKey && (
                                    <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                                      Start frame {startImageKey}
                                    </span>
                                  )}
                                </div>
                                <div className="mt-3 space-y-2">
                                  {beats.slice(0, 3).map((beat, beatIndex) => (
                                    <div key={`${clipKey}-beat-${beatIndex}`} className="rounded-md bg-white px-3 py-2 text-xs leading-relaxed text-slate-600">
                                      <p className="font-semibold text-slate-900">
                                        Beat {numberValue(beat.beat_sort) ?? beatIndex + 1}
                                      </p>
                                      <p className="mt-1">{stringValue(beat.voiceover) || stringValue(beat.prompt) || "Sin detalle"}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          }) : (
                            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                              No hay clips estructurados en el director payload.
                            </div>
                          )}
                        </div>
                      </section>

                      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <h4 className="text-sm font-semibold text-slate-950">Imágenes y start frames</h4>
                        <div className="mt-3 grid grid-cols-2 gap-3 xl:grid-cols-4">
                          {contextImageCards.length > 0 ? contextImageCards.map((image, index) => (
                            <a
                              key={`${image.url}-${index}`}
                              href={image.url}
                              target="_blank"
                              rel="noreferrer"
                              className="group block min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 transition hover:border-primary/40"
                            >
                              <div className="h-36 overflow-hidden bg-white">
                                <img src={image.url} alt="" className="h-full w-full object-cover" />
                              </div>
                              <div className="p-2">
                                <p className="truncate text-xs font-semibold text-slate-900">{image.label}</p>
                                <p className="line-clamp-2 text-[11px] leading-snug text-slate-500">{image.subtitle}</p>
                              </div>
                            </a>
                          )) : (
                            <div className="col-span-full rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                              No hay imágenes guardadas para este contexto.
                            </div>
                          )}
                        </div>
                      </section>

                      <details className="group rounded-xl border border-slate-200 bg-white shadow-sm">
                        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-slate-900 [&::-webkit-details-marker]:hidden">
                          <span>Payload técnico</span>
                          <span className="text-xs font-medium text-slate-500 group-open:hidden">Mostrar JSON</span>
                          <span className="hidden text-xs font-medium text-slate-500 group-open:inline">Ocultar JSON</span>
                        </summary>
                        <div className="border-t border-slate-100 p-4">
                          <div className="mb-3 flex items-center justify-between gap-3">
                            <p className="text-xs text-slate-500">
                              JSON completo para debugging o compartir con ingeniería.
                            </p>
                            <button
                              type="button"
                              onClick={handleCopyContext}
                              disabled={!contextPayloadJson || contextLoading}
                              className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {copiedContext ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                              {copiedContext ? "Copiado" : "Copiar"}
                            </button>
                          </div>
                          <pre className="max-h-80 overflow-auto rounded-xl bg-slate-950 p-4 text-[11px] leading-relaxed text-slate-100 shadow-inner">
                            <code>{contextPayloadJson}</code>
                          </pre>
                        </div>
                      </details>
                    </main>
                  </div>
                </div>
              )}
          </>
        )}
      </motion.div>
    </div>
  );
}

const isSpyEnabled = import.meta.env.VITE_SPY_ENABLED !== "false";
const isVideoStudioEnabled = import.meta.env.VITE_ENABLED_VIDEO === "true";

const formatDateToYMD = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export default function AdsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const initialTab: AdsPageTab =
    rawTab === "video" && isVideoStudioEnabled
      ? "video-studio"
      : rawTab === "scripts" || (rawTab === "spy" && isSpyEnabled) || rawTab === "images"
        ? (rawTab as AdsPageTab)
        : "images";
  const [activeTab, setActiveTab] = useState<AdsPageTab>(initialTab);
  const [spyViewMode, setSpyViewMode] = useState<AdsSpyViewMode>("radar");
  const [spySearchOpenSignal, setSpySearchOpenSignal] = useState(0);
  const { enabled: isCampaignsEnabled } = useMetaCampaignsFlag();
  const [campaignDateStart, setCampaignDateStart] = useState<string | undefined>();
  const [campaignDateEnd, setCampaignDateEnd] = useState<string | undefined>();
  const [campaignDateLabel, setCampaignDateLabel] = useState("");
  const [campaignRefreshKey, setCampaignRefreshKey] = useState(0);
  const [campaignAdAccounts, setCampaignAdAccounts] = useState<MetaAdAccount[]>([]);
  const [campaignSelectedAccount, setCampaignSelectedAccount] = useState<string | null>(null);
  const [campaignAccountDropdownOpen, setCampaignAccountDropdownOpen] = useState(false);
  const [campaignMetaConnected, setCampaignMetaConnected] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateVideoModalOpen, setIsCreateVideoModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewModalIndex, setViewModalIndex] = useState(0);
  const [viewModalEditMode, setViewModalEditMode] = useState(false);
  const [viewVideoIndex, setViewVideoIndex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<CreativeItem[]>([]);
  const { language, credits, setCredits } = useAuth();
  const lang = language as Language;
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [isOpenBuyCreditsModal, setIsOpenBuyCreditsModal] = useState(false);
  const [endMembershipDate, setEndMembershipDate] = useState("");

  useEffect(() => {
    setLoadingCredits(true);
    membershipAxios()
      .then((membership) => {
        setCredits(membership.credits || 0);
        setLoadingCredits(false);
      })
      .catch(() => setLoadingCredits(false));
  }, []);

  useEffect(() => {
    const getStats = async () => {
      const { res } = await handleApi(membershipStatsAxios());
      if (res?.active_membership) {
        setEndMembershipDate(res.active_membership.ends_at);
      }
    };
    getStats();
  }, []);

  // Load ad accounts when campaigns tab is selected
  useEffect(() => {
    if (activeTab !== "campaigns" || !isCampaignsEnabled) return;
    if (campaignAdAccounts.length > 0) return; // already loaded
    const loadAccounts = async () => {
      try {
        const meta = await getMetaIntegration();
        if (!meta?.is_active) return;
        setCampaignMetaConnected(true);
        const accounts = await getMetaAdAccounts(meta.business_id || undefined);
        const list = accounts.adaccounts || [];
        setCampaignAdAccounts(list);
        // Don't auto-select — let user choose from dropdown
      } catch {
        // Not connected
      }
    };
    loadAccounts();
  }, [activeTab, isCampaignsEnabled]);

  // Auto-open dropdown when connected but no account selected
  useEffect(() => {
    if (activeTab === "campaigns" && campaignMetaConnected && campaignAdAccounts.length > 0 && !campaignSelectedAccount) {
      setCampaignAccountDropdownOpen(true);
    }
  }, [activeTab, campaignMetaConnected, campaignAdAccounts, campaignSelectedAccount]);

  const handleCampaignAccountSelect = async (accountId: string) => {
    setCampaignSelectedAccount(accountId);
    setCampaignAccountDropdownOpen(false);
    try {
      await updateMetaAdAccount(accountId);
      setCampaignRefreshKey((k) => k + 1);
    } catch {
      // Error handled in CampaignsTabContent
    }
  };

  const selectedCampaignAccountName = campaignAdAccounts.find((a) => a.id === campaignSelectedAccount)?.name;

  const productId = searchParams.get("product_id");
  const generatingItems = useCreativeGenerationStore((s) => s.items);
  const clearCompleted = useCreativeGenerationStore((s) => s.clearCompleted);
  const videoGeneratingItems = useVideoGenerationStore((s) => s.items);
  const clearVideoCompleted = useVideoGenerationStore((s) => s.clearCompleted);
  // Phase 5.5: in-flight + recently-failed drafts del nuevo flow preview-then-pay.
  // Source of truth = backend (post-approve estados in-flight) + lookup individual
  // para drafts que desaparecieron del listado (puede ser FINISHED → drop, o ERROR
  // → mantener como error card sticky). Polling cada 10s mientras el tab
  // "video-studio" está activo. Memory-only — refresh limpia las cards de error,
  // mismo patrón que creativeGenerationStore para imágenes.
  const inFlightEntries = useVideoInFlightStore((s) => s.entries);
  const inFlightDraftsLoading = useVideoInFlightStore((s) => s.isLoading);
  const loadInFlightDrafts = useVideoInFlightStore((s) => s.loadFromBackend);
  const startInFlightPolling = useVideoInFlightStore((s) => s.startPolling);
  const stopInFlightPolling = useVideoInFlightStore((s) => s.stopPolling);
  // Phase 2 V4 (Apr 18 2026) — Resume an in-flight draft on page load.
  // The previous flow left READY_FOR_REVIEW drafts orphaned after a refresh:
  // `/draft/in-flight` only returns POST-approve states (GENERATING_IMAGE,
  // WAITING_SCENES, MERGING), and the store's `resumeFromStorage` was
  // defined but never invoked by any component. That meant a user who
  // refreshed mid-preview could not retrieve their already-paid draft.
  // We wire it to a single useEffect on mount so the preview modal snaps
  // back into view with the correct state (ready / directing / rendering).
  const resumeDraftFromStorage = useVideoDraftStore((s) => s.resumeFromStorage);
  const activeVideoDraft = useVideoDraftStore((s) => s.draft);
  const resumedDraftStatus = useVideoDraftStore((s) => s.storeStatus);
  // Note: store also exposes `removeDraft` for dismissing error cards.
  // Not wired to any UI yet — error cards persist until refresh, same
  // pattern as creativeGenerationStore for image errors. Add a dismiss
  // button on GeneratingPlaceholderCard in a follow-up PR if needed.
  const [videoItems, setVideoItems] = useState<VideoCreativeItem[]>([]);
  const [videoGalleryLoading, setVideoGalleryLoading] = useState(initialTab === "video-studio");
  const [videoGalleryLoadedOnce, setVideoGalleryLoadedOnce] = useState(false);
  const [visibleVideoCount, setVisibleVideoCount] = useState(VIDEO_GALLERY_PAGE_SIZE);
  const [settlingVideoPlaceholders, setSettlingVideoPlaceholders] = useState<GeneratingItem[]>([]);
  const videoGallerySentinelRef = useRef<HTMLDivElement | null>(null);
  const videoDraftPlaceholderOrderRef = useRef<Map<string, number>>(new Map());
  const nextVideoDraftPlaceholderOrderRef = useRef(0);
  const prevDoneIdsRef = useRef<Set<string>>(new Set());
  const hasLoadedOnce = useRef(false);
  const [products, setProducts] = useState<ProductInfoCreative[]>([]);
  const [scriptsProductFilter, setScriptsProductFilter] = useState<string | null>(null);
  const [scriptsAngleFilter, setScriptsAngleFilter] = useState<string | null>(null);
  const [videoProductFilter, setVideoProductFilter] = useState<string | null>(null);
  const narratives = useAdsPageStore((s) => s.narratives);

  // Derive unique product names and angles from loaded narratives for the Guiones filters
  const narrativeProducts = Array.from(
    new Set(narratives.map((n) => n.product_information?.name).filter((name): name is string => !!name))
  ).map((name) => ({ product_id: name, name, creativesCount: 0 }));

  const narrativeAngles = Array.from(
    new Set(narratives.map((n) => n.sale_angle).filter((a): a is string => !!a))
  );

  const modalsOpenRef = useRef(false);
  modalsOpenRef.current = isCreateModalOpen || isViewModalOpen;

  useEffect(() => {
    creativesService.listProducts().then(setProducts).catch(() => setProducts([]));
  }, []);

  const uniqueProducts = products
    .filter((item) => item.creatives && item.creatives.length > 0)
    .reduce((acc, item) => {
      const existing = acc.find((p) => p.product_id === item.product_id);
      if (existing) {
        existing.creativesCount += item.creatives?.length || 0;
      } else {
        acc.push({
          product_id: item.product_id,
          name: item.name,
          image_url: item.image_url,
          creativesCount: item.creatives?.length || 0,
        });
      }
      return acc;
    }, [] as Array<{ product_id: string; name: string; image_url?: string; creativesCount: number }>);

  const handleProductFilter = (id: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (id) {
      params.set("product_id", id);
    } else {
      params.delete("product_id");
    }
    setSearchParams(params);
  };

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const mapped = tabParam === "video" ? "video-studio" : tabParam;
    const validTabs = ["images", "scripts", ...(isVideoStudioEnabled ? ["video-studio"] : []), ...(isSpyEnabled ? ["spy"] : [])];
    if (mapped && validTabs.includes(mapped)) {
      setActiveTab(mapped as AdsPageTab);
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeTab !== "images") return;

    const loadCreatives = async () => {
      if (!hasLoadedOnce.current) {
        setLoading(true);
      }
      try {
        const data = await creativesService.listByProductId(productId || undefined);
        setItems(data.filter((c) => !c.creative_type || c.creative_type === "IMAGE"));
      } catch (error) {
        console.error("Error loading creatives:", error);
        setItems([]);
      } finally {
        setLoading(false);
        hasLoadedOnce.current = true;
      }
    };

    loadCreatives();
  }, [productId, activeTab]);

  // Re-fetch each time an individual item finishes, then clean up done items
  useEffect(() => {
    if (activeTab !== "images") return;

    const doneItems = generatingItems.filter((i) => i.status === "done");
    const newlyDone = doneItems.filter((i) => !prevDoneIdsRef.current.has(i.id));

    if (newlyDone.length === 0) return;

    // Track which done items we've already processed
    newlyDone.forEach((i) => prevDoneIdsRef.current.add(i.id));

    const reload = async () => {
      try {
        const data = await creativesService.listByProductId(productId || undefined);
        setItems(data.filter((c) => !c.creative_type || c.creative_type === "IMAGE"));
      } catch (error) {
        console.error("Error reloading creatives:", error);
      }

      // After real cards are loaded, remove done placeholders with a short delay
      // so the user briefly sees the "Listo" success state
      setTimeout(() => {
        clearCompleted();
        // Reset tracking when no items remain
        const remaining = useCreativeGenerationStore.getState().items;
        if (remaining.length === 0) {
          prevDoneIdsRef.current.clear();
        }
      }, 1500);
    };

    reload();
  }, [generatingItems, activeTab, productId, clearCompleted]);

  // Separate backend items into ready (has image) and pending (generating on backend)
  const storeBackendIds = useMemo(
    () => new Set(generatingItems.filter((i) => i.backendId).map((i) => i.backendId)),
    [generatingItems]
  );

  const imageItems = useMemo(
    () => items.filter((item) => !item.creative_type || item.creative_type === "IMAGE"),
    [items]
  );

  const pendingBackendItems = useMemo(
    () => imageItems.filter((item) =>
      !item.image_url &&
      item.id != null &&
      item.status !== "FAILED" &&
      !storeBackendIds.has(item.id)
    ),
    [imageItems, storeBackendIds]
  );

  const readyItems = useMemo(
    () => imageItems.filter((item) => !!item.image_url),
    [imageItems]
  );

  // Convert pending backend items to GeneratingItem format for placeholder cards
  const pendingAsGenerating: GeneratingItem[] = useMemo(
    () =>
      pendingBackendItems.map((item) => ({
        id: `backend-${item.id}`,
        backendId: item.id!,
        status: "generating" as const,
        format: item.format,
        productName: item.product_name || "",
        productImageUrl: item.product_image_url || undefined,
      })),
    [pendingBackendItems]
  );

  // Auto-refresh when there are pending backend items (e.g. after page reload)
  const reloadCreatives = useCallback(async () => {
    try {
      const data = await creativesService.listByProductId(productId || undefined);
      setItems(data.filter((c) => !c.creative_type || c.creative_type === "IMAGE"));
    } catch (error) {
      console.error("Error reloading creatives:", error);
    }
  }, [productId]);

  const pendingPollAttemptsRef = useRef(0);
  const MAX_PENDING_POLL_ATTEMPTS = 24; // 2 minutes max (24 * 5s)

  useEffect(() => {
    if (activeTab !== "images") return;
    if (pendingBackendItems.length === 0) {
      pendingPollAttemptsRef.current = 0;
      return;
    }

    if (pendingPollAttemptsRef.current >= MAX_PENDING_POLL_ATTEMPTS) return;

    const interval = setInterval(() => {
      pendingPollAttemptsRef.current++;
      if (pendingPollAttemptsRef.current >= MAX_PENDING_POLL_ATTEMPTS) {
        clearInterval(interval);
        // Remove stale pending items by filtering them out
        setItems((prev) => prev.filter((item) => !!item.image_url));
        return;
      }
      reloadCreatives();
    }, 5000);
    return () => clearInterval(interval);
  }, [activeTab, pendingBackendItems.length, reloadCreatives]);

  // Load video creatives for the video-studio tab
  const prevVideoDoneIdsRef = useRef<Set<string>>(new Set());
  const prevInFlightDraftPlaceholdersRef = useRef<Map<string, GeneratingItem>>(new Map());
  const videoGalleryRequestSeqRef = useRef(0);

  const mapSceneVideosToCreativeItems = useCallback((videos: VideoStatusResponse[]): VideoCreativeItem[] => {
    return videos
      .filter((v) => v.overall_status === "finished" && v.video_url)
      .map((v, index) => ({
        id: 900000 + index,
        product_id: v.product_id,
        image_url: v.product_image_url || v.base_image_url || "",
        video_url: v.video_url,
        base_image_url: v.base_image_url,
        creative_type: "VIDEO",
        format: "9:16",
        status: "COMPLETED",
        product_name: v.product_name || v.video_content_type,
        product_image_url: v.product_image_url,
        reference_id: v.reference_id,
      }));
  }, []);

  const loadVideoGallery = useCallback(async (): Promise<VideoStatusResponse[]> => {
    const requestSeq = ++videoGalleryRequestSeqRef.current;
    const sceneVideosPromise = listVideos().catch((error) => {
      console.error("Error loading generated videos:", error);
      return [] as VideoStatusResponse[];
    });
    const legacyVideosPromise = creativesService.listByProductId().catch((error) => {
      console.error("Error loading legacy video creatives:", error);
      return [] as Awaited<ReturnType<typeof creativesService.listByProductId>>;
    });

    const sceneVideos = await sceneVideosPromise;
    const newSystemVideos = mapSceneVideosToCreativeItems(sceneVideos);
    if (requestSeq === videoGalleryRequestSeqRef.current) {
      setVideoItems(newSystemVideos);
    }

    legacyVideosPromise.then((items) => {
      if (requestSeq !== videoGalleryRequestSeqRef.current) return;
      const legacyVideos = items.filter((c) => c.creative_type === "VIDEO");
      setVideoItems([...newSystemVideos, ...legacyVideos]);
    });

    return sceneVideos;
  }, [mapSceneVideosToCreativeItems]);

  useEffect(() => {
    if (activeTab !== "video-studio") return;
    let cancelled = false;
    const loadVideos = async () => {
      setVideoGalleryLoading(true);
      try {
        await loadVideoGallery();
      } catch (error) {
        console.error("Error loading video creatives:", error);
      } finally {
        if (!cancelled) {
          setVideoGalleryLoading(false);
          setVideoGalleryLoadedOnce(true);
        }
      }
    };
    loadVideos();
    return () => {
      cancelled = true;
    };
  }, [activeTab, loadVideoGallery]);

  // Phase 5.5: in-flight video drafts loading + polling. Independent of the
  // legacy creatives fetch above — this is the new draft flow's source of
  // truth (preview-then-pay). On video-studio tab activation we fetch fresh
  // and start the 10s polling. On unmount/tab change we stop polling. The
  // store also caches to localStorage so the gallery is not empty during
  // the first request after a page refresh.
  useEffect(() => {
    if (activeTab !== "video-studio") return;
    loadInFlightDrafts();
    startInFlightPolling();
    // Rescue orphaned READY_FOR_REVIEW drafts after a page refresh.
    // Fire once per tab activation — the store short-circuits if there's
    // no reference_id in localStorage. See resumeDraftFromStorage comment
    // above for the architectural reason.
    resumeDraftFromStorage();
    return () => {
      stopInFlightPolling();
    };
  }, [activeTab, loadInFlightDrafts, startInFlightPolling, stopInFlightPolling, resumeDraftFromStorage]);

  // Phase 2 V4 (Apr 18 2026) — auto-open the video modal when a draft is
  // resumed from storage. Without this, resumeDraftFromStorage hydrates the
  // store but the CreateVideoModal stays closed (its `open` prop is a local
  // useState controlled by the "Crear Video con IA" button). The watch
  // fires ONCE per status transition from "idle" → any active state.
  //
  // Phase 2 V8 (Apr 19 evening — BUG FIX) — post-approve states
  // ("approving", "rendering", "finished") were removed from this set. The
  // old list caused a nasty loop: user clicks "Crear mi video" →
  // PreviewApproval.onDone() closes the modal optimistically → the store
  // flips to "rendering" → THIS watcher saw status ∈ activeStates AND
  // modal closed, and re-opened it. The reopened modal had no
  // initialProduct, so CreateVideoModal's own useEffect reset step to 1
  // and showed "Selecciona un producto", yanking the user back to the
  // product picker instead of the rendering gallery card.
  //
  // Fix: the watcher only reopens for PRE-approve states (director running,
  // preview ready, user editing in the modal). Post-approve progress is
  // tracked by the gallery's loading card, not by re-opening the modal.
  useEffect(() => {
    if (activeTab !== "video-studio") return;
    const preApproveActiveStates = new Set([
      "directing",
      "ready",
      "editing_script",
      "regenerating_image",
      "regenerating_style",
    ]);
    if (preApproveActiveStates.has(resumedDraftStatus) && !isCreateVideoModalOpen) {
      setIsCreateVideoModalOpen(true);
    }
  }, [activeTab, resumedDraftStatus, isCreateVideoModalOpen]);

  // Auto-reload video creatives when generation completes
  useEffect(() => {
    if (activeTab !== "video-studio") return;
    const doneItems = videoGeneratingItems.filter((i) => i.status === "done");
    const newlyDone = doneItems.filter((i) => !prevVideoDoneIdsRef.current.has(i.id));
    if (newlyDone.length === 0) return;
    newlyDone.forEach((i) => prevVideoDoneIdsRef.current.add(i.id));

    // Phase 2 V9 REV 2 (Apr 19 2026) — user reported that after the
    // "generating" card disappears, the actual video doesn't show in the
    // gallery until they manually refresh the page. Root cause: the poller
    // detects state=FINISHED via /ads/videos/status, but there's a small
    // window where /ads/videos/list hasn't caught up — either video_url is
    // still being written from the merge worker, or getVideosByOwner reads
    // a replica with latency. Our reload fired ONCE immediately and swallowed
    // that race.
    //
    // Fix: fetch the list up to 3 times with backoff until the new video's
    // reference_id appears in the response. If it never shows up (shouldn't
    // happen but be safe), fall through after ~10s so the UI isn't stuck.
    const reload = async () => {
      const targetRefIds = new Set(
        newlyDone.map((i) => i.referenceId).filter((r): r is string => !!r)
      );
      const fetchAndApply = async (): Promise<boolean> => {
        try {
          const sceneVideos = await loadVideoGallery();
          // "Got it" = every just-finished draft is in the response with a
          // video_url set. If any is missing, we'll retry.
          if (targetRefIds.size === 0) return true;
          const foundRefIds = new Set(
            sceneVideos
              .filter((v) => v.overall_status === "finished" && v.video_url && v.reference_id)
              .map((v) => v.reference_id as string)
          );
          return Array.from(targetRefIds).every((r) => foundRefIds.has(r));
        } catch (error) {
          console.error("Error reloading video creatives:", error);
          return false;
        }
      };

      // Backoff: 0ms, 2s, 5s. Stops as soon as all target refIds appear.
      const delays = [0, 2000, 5000];
      for (let i = 0; i < delays.length; i++) {
        if (delays[i] > 0) await new Promise((r) => setTimeout(r, delays[i]));
        const ok = await fetchAndApply();
        if (ok) break;
      }

      // Clear the "generating" card AFTER the gallery has the new video,
      // so the user never sees an empty gallery between disappearance and
      // video showing up.
      setTimeout(() => {
        clearVideoCompleted();
        const remaining = useVideoGenerationStore.getState().items;
        if (remaining.length === 0) prevVideoDoneIdsRef.current.clear();
      }, 500);
    };
    reload();
  }, [videoGeneratingItems, activeTab, clearVideoCompleted, loadVideoGallery]);

  // New draft flow: when the in-flight card disappears because the backend
  // reached FINISHED, refresh the gallery automatically. Without this, the
  // user saw the loading card go away but had to reload the page to see the
  // final video card.
  useEffect(() => {
    if (activeTab !== "video-studio") return;

    const currentTrackedRefs = new Set(
      inFlightEntries
        .map((entry) => entry.draft.reference_id)
        .filter((ref): ref is string => !!ref)
    );
    const currentPlaceholders = new Map<string, GeneratingItem>();
    for (const entry of inFlightEntries) {
      if (entry.displayStatus !== "in-flight" && entry.displayStatus !== "preparing") continue;
      const ref = entry.draft.reference_id;
      if (!ref) continue;
      currentPlaceholders.set(ref, {
        id: `draft-settling-${ref}`,
        backendId: undefined,
        status: "generating" as const,
        format: "9:16",
        productName: entry.draft.product_name || "",
        productImageUrl: entry.draft.product_image_url || undefined,
      });
    }

    const previousPlaceholders = prevInFlightDraftPlaceholdersRef.current;

    if (previousPlaceholders.size === 0) {
      prevInFlightDraftPlaceholdersRef.current = currentPlaceholders;
      return;
    }

    const completedRefs = Array.from(previousPlaceholders.keys()).filter(
      (ref) => !currentTrackedRefs.has(ref)
    );
    const completedPlaceholders = completedRefs
      .map((ref) => previousPlaceholders.get(ref))
      .filter((item): item is GeneratingItem => !!item);
    prevInFlightDraftPlaceholdersRef.current = currentPlaceholders;
    if (completedRefs.length === 0) return;

    setSettlingVideoPlaceholders((current) => {
      const byId = new Map(current.map((item) => [item.id, item]));
      completedPlaceholders.forEach((item) => byId.set(item.id, item));
      return Array.from(byId.values());
    });

    let cancelled = false;
    const removeFoundPlaceholders = (sceneVideos: VideoStatusResponse[]) => {
      const foundRefIds = new Set(
        sceneVideos
          .filter((v) => v.overall_status === "finished" && v.video_url && v.reference_id)
          .map((v) => v.reference_id as string)
      );
      setSettlingVideoPlaceholders((current) =>
        current.filter((item) => {
          const referenceId = item.id.replace(/^draft-settling-/, "");
          return !foundRefIds.has(referenceId);
        })
      );
      return foundRefIds;
    };

    const reload = async () => {
      const delays = [0, 2000, 5000, 10000, 20000];
      for (const delay of delays) {
        if (delay > 0) await new Promise((resolve) => setTimeout(resolve, delay));
        if (cancelled) return;
        try {
          const sceneVideos = await loadVideoGallery();
          const foundRefIds = removeFoundPlaceholders(sceneVideos);
          if (completedRefs.every((ref) => foundRefIds.has(ref))) return;
        } catch (error) {
          console.error("Error reloading finished draft videos:", error);
        }
      }
    };

    reload();
    return () => {
      cancelled = true;
    };
  }, [activeTab, inFlightEntries, loadVideoGallery]);

  useEffect(() => {
    if (activeTab !== "video-studio" || settlingVideoPlaceholders.length === 0) return;
    let cancelled = false;
    const refs = new Set(
      settlingVideoPlaceholders.map((item) => item.id.replace(/^draft-settling-/, ""))
    );

    const interval = window.setInterval(async () => {
      try {
        const sceneVideos = await loadVideoGallery();
        if (cancelled) return;
        const foundRefIds = new Set(
          sceneVideos
            .filter((v) => v.overall_status === "finished" && v.video_url && v.reference_id)
            .map((v) => v.reference_id as string)
        );
        setSettlingVideoPlaceholders((current) =>
          current.filter((item) => !foundRefIds.has(item.id.replace(/^draft-settling-/, "")))
        );
        if (Array.from(refs).every((ref) => foundRefIds.has(ref))) {
          window.clearInterval(interval);
        }
      } catch (error) {
        console.error("Error checking settling video placeholders:", error);
      }
    }, 10_000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [activeTab, loadVideoGallery, settlingVideoPlaceholders]);

  const readyVideoItems = useMemo(
    () => videoItems.filter((item) => !!item.video_url || !!item.base_image_url),
    [videoItems]
  );

  const uniqueVideoProducts = useMemo(() => {
    const map = new Map<string, { product_id: string; name: string; image_url?: string; creativesCount: number }>();
    for (const item of videoItems) {
      if (!item.product_name) continue;
      const key = item.product_id || item.product_name;
      const existing = map.get(key);
      if (existing) {
        existing.creativesCount += 1;
      } else {
        map.set(key, {
          product_id: key,
          name: item.product_name,
          image_url: item.product_image_url,
          creativesCount: 1,
        });
      }
    }
    return Array.from(map.values());
  }, [videoItems]);

  const filteredReadyVideoItems = useMemo(
    () => videoProductFilter
      ? readyVideoItems.filter((item) => (item.product_id || item.product_name) === videoProductFilter)
      : readyVideoItems,
    [readyVideoItems, videoProductFilter]
  );

  useEffect(() => {
    setVisibleVideoCount(VIDEO_GALLERY_PAGE_SIZE);
  }, [videoProductFilter]);

  const visibleReadyVideoItems = useMemo(
    () => filteredReadyVideoItems.slice(0, visibleVideoCount),
    [filteredReadyVideoItems, visibleVideoCount]
  );

  const hasMoreReadyVideos = visibleVideoCount < filteredReadyVideoItems.length;

  const loadMoreReadyVideos = useCallback(() => {
    setVisibleVideoCount((current) =>
      Math.min(current + VIDEO_GALLERY_PAGE_SIZE, filteredReadyVideoItems.length)
    );
  }, [filteredReadyVideoItems.length]);

  useEffect(() => {
    if (activeTab !== "video-studio" || !hasMoreReadyVideos) return;
    const sentinel = videoGallerySentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreReadyVideos();
        }
      },
      { rootMargin: "260px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [activeTab, hasMoreReadyVideos, loadMoreReadyVideos]);

  useEffect(() => {
    if (activeTab !== "video-studio" || !hasMoreReadyVideos) return;
    const sentinel = videoGallerySentinelRef.current;
    if (!sentinel) return;

    const checkIfAlreadyVisible = () => {
      const rect = sentinel.getBoundingClientRect();
      if (rect.top <= window.innerHeight + 260) {
        loadMoreReadyVideos();
      }
    };

    checkIfAlreadyVisible();
    const timeout = window.setTimeout(checkIfAlreadyVisible, 150);
    return () => window.clearTimeout(timeout);
  }, [activeTab, hasMoreReadyVideos, loadMoreReadyVideos, visibleVideoCount, filteredReadyVideoItems.length]);

  // --- Video persistence after refresh (mirrors image pendingBackendItems pattern) ---
  const videoStoreBackendIds = useMemo(
    () => new Set(videoGeneratingItems.filter((i) => i.backendId).map((i) => i.backendId)),
    [videoGeneratingItems]
  );

  const pendingVideoBackendItems = useMemo(
    () => videoItems.filter((item) =>
      item.creative_type === "VIDEO" &&
      !item.video_url &&
      item.id != null &&
      item.status !== "FAILED" &&
      !videoStoreBackendIds.has(item.id)
    ),
    [videoItems, videoStoreBackendIds]
  );

  const pendingVideoAsGenerating: GeneratingItem[] = useMemo(
    () =>
      pendingVideoBackendItems.map((item) => ({
        id: `backend-video-${item.id}`,
        backendId: item.id!,
        status: "generating" as const,
        format: item.format || "9:16",
        productName: item.product_name || "",
        productImageUrl: item.product_image_url || undefined,
      })),
    [pendingVideoBackendItems]
  );

  const getStableDraftPlaceholderOrder = useCallback((referenceId: string) => {
    const existing = videoDraftPlaceholderOrderRef.current.get(referenceId);
    if (existing !== undefined) return existing;
    const next = nextVideoDraftPlaceholderOrderRef.current;
    nextVideoDraftPlaceholderOrderRef.current += 1;
    videoDraftPlaceholderOrderRef.current.set(referenceId, next);
    return next;
  }, []);

  const activeDraftAsGenerating: GeneratingItem[] = useMemo(() => {
    if (!activeVideoDraft) return [];
    const galleryStatuses = new Set(["generating_audio", "approving", "rendering"]);
    if (!galleryStatuses.has(resumedDraftStatus)) return [];

    return [
      {
        id: `draft-active-${activeVideoDraft.reference_id}`,
        backendId: undefined,
        status: "generating" as const,
        format: "9:16",
        productName: activeVideoDraft.product_name || "",
        productImageUrl: activeVideoDraft.product_image_url || undefined,
      },
    ];
  }, [activeVideoDraft, resumedDraftStatus]);

  const activeGalleryDraftReferenceId =
    activeDraftAsGenerating.length > 0 ? activeVideoDraft?.reference_id ?? null : null;

  // Phase 5.5: map in-flight entries (from the new draft flow) to the same
  // GeneratingItem shape used by GeneratingPlaceholderCard so they render
  // alongside the legacy generating cards.
  //
  // The store has TWO display states per entry:
  //   - "in-flight" → render as loading card with spinner ("generating")
  //   - "error"     → render as red error card with errorMessage ("error")
  //
  // GeneratingPlaceholderCard already handles both via its `status` prop —
  // we just need to translate. The error card lives in memory until refresh
  // OR until the user dismisses it (mismo patrón que creativeGenerationStore
  // para imágenes).
  //
  // Dedupe note: in-flight drafts use `reference_id` as their unique key,
  // and pendingVideoBackendItems use `id` (creative id). They never share
  // an id namespace so adding both is safe.
  const inFlightDraftsAsGenerating: GeneratingItem[] = useMemo(
    () =>
      inFlightEntries
        .filter((entry) => entry.draft.reference_id !== activeGalleryDraftReferenceId)
        .slice()
        .sort((a, b) => {
          const aOrder = getStableDraftPlaceholderOrder(a.draft.reference_id);
          const bOrder = getStableDraftPlaceholderOrder(b.draft.reference_id);
          return bOrder - aOrder;
        })
        .map((entry) => ({
          id: `draft-in-flight-${entry.draft.reference_id}`,
          backendId: undefined,
          status: entry.displayStatus === "error" ? ("error" as const) : ("generating" as const),
          format: "9:16",
          productName: entry.draft.product_name || "",
          productImageUrl: entry.draft.product_image_url || undefined,
          errorMessage: entry.draft.error_message || undefined,
        })),
    [inFlightEntries, activeGalleryDraftReferenceId, getStableDraftPlaceholderOrder]
  );

  const draftVideoPlaceholderCards = useMemo(() => {
    const byReference = new Map<string, GeneratingItem>();
    [...activeDraftAsGenerating, ...inFlightDraftsAsGenerating, ...settlingVideoPlaceholders].forEach((item) => {
      const referenceId = getDraftPlaceholderReferenceId(item);
      if (!byReference.has(referenceId)) {
        byReference.set(referenceId, item);
      }
    });

    return Array.from(byReference.entries())
      .sort(([aRef], [bRef]) => getStableDraftPlaceholderOrder(bRef) - getStableDraftPlaceholderOrder(aRef))
      .map(([, item]) => item);
  }, [
    activeDraftAsGenerating,
    inFlightDraftsAsGenerating,
    settlingVideoPlaceholders,
    getStableDraftPlaceholderOrder,
  ]);

  const videoGalleryCardCount =
    videoGeneratingItems.length +
    pendingVideoAsGenerating.length +
    draftVideoPlaceholderCards.length +
    filteredReadyVideoItems.length;
  const showVideoGallerySkeleton =
    activeTab === "video-studio" &&
    !videoGalleryLoadedOnce &&
    (videoGalleryLoading || inFlightDraftsLoading);

  // Polling post-refresh for pending video items
  const reloadVideoCreatives = useCallback(async () => {
    try {
      await loadVideoGallery();
    } catch (error) {
      console.error("Error reloading video creatives:", error);
    }
  }, [loadVideoGallery]);

  const pendingVideoPollAttemptsRef = useRef(0);
  const MAX_PENDING_VIDEO_POLL_ATTEMPTS = 80; // 20 min max (80 * 15s)

  useEffect(() => {
    if (activeTab !== "video-studio") return;
    if (pendingVideoBackendItems.length === 0) {
      pendingVideoPollAttemptsRef.current = 0;
      return;
    }

    if (pendingVideoPollAttemptsRef.current >= MAX_PENDING_VIDEO_POLL_ATTEMPTS) return;

    const interval = setInterval(() => {
      pendingVideoPollAttemptsRef.current++;
      if (pendingVideoPollAttemptsRef.current >= MAX_PENDING_VIDEO_POLL_ATTEMPTS) {
        clearInterval(interval);
        setVideoItems((prev) => prev.filter((item) => !!item.video_url || !!item.base_image_url));
        return;
      }
      reloadVideoCreatives();
    }, 15_000);
    return () => clearInterval(interval);
  }, [activeTab, pendingVideoBackendItems.length, reloadVideoCreatives]);

  const totalPlaceholders = generatingItems.length + pendingAsGenerating.length;

  const handleViewCreative = (index: number) => {
    setViewModalIndex(index - totalPlaceholders);
    setViewModalEditMode(false);
    setIsViewModalOpen(true);
  };

  const handleEditCreative = (index: number) => {
    setViewModalIndex(index - totalPlaceholders);
    setViewModalEditMode(true);
    setIsViewModalOpen(true);
  };

  const renderCreativesContent = () => {
    if (loading && totalPlaceholders === 0) {
      return <CreativesGridSkeleton />;
    }

    const showEmptyState = !loading && readyItems.length === 0 && totalPlaceholders === 0;

    if (showEmptyState) {
      return (
        <div className="mt-4 bg-white border border-gray-200 rounded-2xl p-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-50 mb-4">
            <Sparkles className="h-7 w-7 text-indigo-400" />
          </div>
          <p className="text-gray-700 font-semibold">
            {dictionary["no-creatives-available"]?.[language as Language] || "Aún no tienes creativos disponibles."}
          </p>
          <p className="text-gray-500 text-sm mt-2">
            {dictionary["create-a-creative-first"]?.[language as Language] || "Crea tu primer anuncio con inteligencia artificial."}
          </p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
          >
            <Sparkles className="h-4 w-4" />
            {dictionary["create-new-ad-with-ai"]?.[language as Language] || "Crear Imagen con IA"}
          </button>
        </div>
      );
    }

    return (
      <div
        className="grid gap-6"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}
      >
        {/* Placeholder cards from in-memory store (current session generation) */}
        {generatingItems.map((item) => (
          <GeneratingPlaceholderCard key={item.id} item={item} />
        ))}
        {/* Placeholder cards from backend pending items (after page reload) */}
        {pendingAsGenerating.map((item) => (
          <GeneratingPlaceholderCard key={item.id} item={item} />
        ))}
        {/* Ready creatives with images */}
        {readyItems.map((item, index) => (
          <CreativeCard
            key={item.id != null ? String(item.id) : item.image_url}
            item={item}
            index={index + totalPlaceholders}
            onClick={() => {
              console.log("Creative clicked:", item.name || item.product_name);
            }}
            onView={handleViewCreative}
            onEdit={handleEditCreative}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      {isOpenBuyCreditsModal && (
        <ModalWrapper isOpen={isOpenBuyCreditsModal} onClose={() => setIsOpenBuyCreditsModal(false)}>
          <BuyCreditsModal
            onClose={() => setIsOpenBuyCreditsModal(false)}
            nextRenewalDate={endMembershipDate}
            language={lang}
          />
        </ModalWrapper>
      )}
    <div className="min-h-screen bg-[#F5F6FA] px-16 pt-10 pb-16 font-['Plus_Jakarta_Sans']">
      <h1 className="text-2xl font-bold tracking-tight mb-6">{dictionary["ads"]?.[lang] ?? "Anuncios"}</h1>
      {/* Tabs + filters + action button — single row */}
      <div className={activeTab === "campaigns" ? "pb-4" : "pb-8"}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-gray-200 rounded-full p-1">
              <button
                onClick={() => { setActiveTab("images"); setSearchParams((p) => { p.set("tab", "images"); return p; }); }}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeTab === "images"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Imágenes
              </button>
              {isVideoStudioEnabled && (
                <button
                  onClick={() => { setActiveTab("video-studio"); setSearchParams((p) => { p.set("tab", "video"); return p; }); }}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    activeTab === "video-studio"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Videos
                </button>
              )}
              <button
                onClick={() => { setActiveTab("scripts"); setSearchParams((p) => { p.set("tab", "scripts"); return p; }); }}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeTab === "scripts"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Guiones
              </button>
              {isSpyEnabled && (
                <button
                  onClick={() => { setActiveTab("spy"); setSearchParams((p) => { p.set("tab", "spy"); return p; }); }}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    activeTab === "spy"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Espía
                </button>
              )}
            </div>

            {activeTab === "images" && (
              <ProductFilterDropdown
                uniqueProducts={uniqueProducts}
                selectedProductId={productId}
                onSelect={handleProductFilter}
                language={language as Language}
              />
            )}

            {activeTab === "scripts" && (
              <div className="flex items-center gap-2">
                <ProductFilterDropdown
                  uniqueProducts={narrativeProducts}
                  selectedProductId={scriptsProductFilter}
                  onSelect={setScriptsProductFilter}
                  language={language as Language}
                />
                <AngleFilterDropdown
                  angles={narrativeAngles}
                  selectedAngle={scriptsAngleFilter}
                  onSelect={setScriptsAngleFilter}
                />
              </div>
            )}

            {activeTab === "video-studio" && (
              <ProductFilterDropdown
                uniqueProducts={uniqueVideoProducts}
                selectedProductId={videoProductFilter}
                onSelect={setVideoProductFilter}
                language={language as Language}
              />
            )}

          </div>

          {/* Ad Account Dropdown — centered between tabs and right controls */}
          {activeTab === "campaigns" && campaignAdAccounts.length > 0 && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setCampaignAccountDropdownOpen(!campaignAccountDropdownOpen)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border whitespace-nowrap",
                  !campaignSelectedAccount
                    ? "bg-white text-fluxi border-fluxi shadow-sm ring-2 ring-fluxi/30"
                    : "bg-white text-fluxi border-fluxi shadow-sm"
                )}
              >
                <span className="max-w-[200px] truncate">
                  {selectedCampaignAccountName || (dictionary["campaigns-select-account-short"]?.[language as Language] ?? "Seleccionar cuenta")}
                </span>
                <ChevronDown className={cn("h-3.5 w-3.5 flex-shrink-0 transition-transform", campaignAccountDropdownOpen && "rotate-180")} />
              </button>

              <AnimatePresence>
                {campaignAccountDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-20" aria-hidden onClick={() => setCampaignAccountDropdownOpen(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: 4, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 4, scale: 0.96 }}
                      transition={{ duration: 0.15, ease: "easeOut" }}
                      className="absolute top-full mt-2 z-30 w-72 bg-white border border-gray-200/40 rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto left-0"
                    >
                      {campaignAdAccounts.map((account) => (
                        <button
                          key={account.id}
                          type="button"
                          onClick={() => handleCampaignAccountSelect(account.id)}
                          className={cn(
                            "w-full flex flex-col px-4 py-3 text-sm text-left transition-colors",
                            campaignSelectedAccount === account.id
                              ? "bg-primary/5 text-primary font-medium"
                              : "text-gray-900 hover:bg-gray-50"
                          )}
                        >
                          <span className="truncate">{account.name}</span>
                          <span className="text-xs text-gray-400">{account.id}</span>
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          )}

          {activeTab === "campaigns" ? (
          <div className="flex items-center gap-3">
            {/* Date filter — styled to match pills, blue when active, with clear button */}
            <div className="flex items-center gap-1">
              <div className={cn(
                "[&_.absolute]:right-0 [&_.absolute]:left-auto",
                "[&>div>button]:!h-auto [&>div>button]:!py-2 [&>div>button]:!rounded-full [&>div>button]:!text-sm [&>div>button]:!font-medium",
                campaignDateLabel
                  ? "[&>div>button]:!border-fluxi [&>div>button]:!text-fluxi [&>div>button]:shadow-sm"
                  : "[&>div>button]:!border-gray-200 [&>div>button]:!text-gray-700"
              )}>
                <DateFilter onFilterApply={(start, end, label) => {
                  setCampaignDateStart(formatDateToYMD(start));
                  setCampaignDateEnd(formatDateToYMD(end));
                  setCampaignDateLabel(label);
                }} />
              </div>
              {campaignDateLabel && (
                <button
                  type="button"
                  onClick={() => {
                    setCampaignDateStart(undefined);
                    setCampaignDateEnd(undefined);
                    setCampaignDateLabel("");
                  }}
                  className="flex items-center justify-center w-6 h-6 rounded-full bg-fluxi/10 text-fluxi hover:bg-fluxi/20 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <button
              className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              onClick={() => setCampaignRefreshKey((k) => k + 1)}
            >
              <RefreshCw className="w-4 h-4 text-gray-600" />
            </button>
          </div>
          ) : activeTab === "spy" ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSpyViewMode((mode) => mode === "favorites" ? "radar" : "favorites")}
              className={cn(
                "inline-flex h-11 w-11 items-center justify-center rounded-full border text-gray-600 shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-fluxi/40",
                spyViewMode === "favorites"
                  ? "border-fluxi bg-fluxi text-white"
                  : "border-gray-200 bg-white hover:bg-gray-50 hover:text-gray-900"
              )}
              title="Favoritos"
              aria-label="Favoritos"
            >
              <Star className="h-[18px] w-[18px]" fill={spyViewMode === "favorites" ? "currentColor" : "none"} />
            </button>
            <button
              type="button"
              onClick={() => {
                setSpyViewMode("radar");
                setSpySearchOpenSignal((key) => key + 1);
              }}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-fluxi/40"
              title="Buscar"
              aria-label="Buscar"
            >
              <Search className="h-[18px] w-[18px]" />
            </button>
          </div>
          ) : activeTab === "images" || activeTab === "video-studio" ? (
          <div className="flex items-center gap-3">
            <Chip
              text={dictionary["credits"]?.[language as Language] ?? "Créditos"}
              value={credits.toString()}
              onClick={() => setIsOpenBuyCreditsModal(true)}
              loading={loadingCredits}
              compact
            />
            <ButtonFluxi
              text={activeTab === "video-studio" ? (dictionary["create-video-with-ai"]?.[language as Language] ?? "Create Video with AI") : (dictionary["create-new-ad-with-ai"]?.[language as Language] || "Crear Imagen con IA")}
              onClick={() => activeTab === "video-studio" ? setIsCreateVideoModalOpen(true) : setIsCreateModalOpen(true)}
              fullWidth={false}
            >
              <Plus className="h-4 w-4 relative z-10" />
            </ButtonFluxi>
          </div>
          ) : (
          <div className="h-11" />
          )}
        </div>
      </div>

      {/* Date label below tabs when campaigns is active */}
      {activeTab === "campaigns" && campaignDateLabel && (
        <p className="text-xs text-gray-400 mb-1">
          {dictionary["campaigns-showing"]?.[language as Language] ?? "Mostrando"}: {campaignDateLabel}
        </p>
      )}

      {/* Images tab — constrained width, normal scroll */}
      <div className={`w-full pb-8 ${activeTab !== "images" ? "hidden" : ""}`}>
        {renderCreativesContent()}

        <CreateAdWithIAModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          initialStep={1}
        />

        <ViewCreativeModal
          open={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          creatives={readyItems}
          initialIndex={viewModalIndex}
          language={language as Language}
          initialEditMode={viewModalEditMode}
          onCreativeUpdated={(id, newImageUrl) => {
            setItems((prev) =>
              prev.map((item) =>
                item.id === id ? { ...item, image_url: newImageUrl } : item
              )
            );
          }}
        />
      </div>

      {/* Scripts tab — embedded layout, no duplicate header */}
      {activeTab === "scripts" && (
        <div className="flex-1 flex flex-col min-h-0">
          <HyphensTabContent hideActions embedded externalProductFilter={scriptsProductFilter} externalAngleFilter={scriptsAngleFilter} />
        </div>
      )}

      {/* Videos tab */}
      {activeTab === "video-studio" && (
        <div className="w-full pb-8 ">
          {showVideoGallerySkeleton ? (
            <VideoGallerySkeleton />
          ) : videoGalleryCardCount === 0 ? (
            <div className="mt-4 bg-white border border-gray-200 rounded-2xl p-10 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-50 mb-4">
                <Video className="h-7 w-7 text-indigo-400" />
              </div>
              <p className="text-gray-700 font-semibold">
                {dictionary["video-empty-title"]?.[language as Language]}
              </p>
              <p className="text-gray-500 text-sm mt-2">
                {dictionary["video-empty-subtitle"]?.[language as Language]}
              </p>
              <button
                onClick={() => setIsCreateVideoModalOpen(true)}
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
              >
                <Sparkles className="h-4 w-4" />
                Crear Video con IA
              </button>
            </div>
          ) : (
            <>
              <div
                className="grid gap-6 mt-4"
                style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}
              >
                {videoGeneratingItems.map((item) => (
                  <GeneratingPlaceholderCard
                    key={item.id}
                    isVideo
                    item={{
                      id: item.id,
                      backendId: item.backendId,
                      status: item.status,
                      imageUrl: item.baseImageUrl,
                      format: item.format,
                      productName: item.productName,
                      productImageUrl: item.productImageUrl,
                      errorMessage: item.errorMessage,
                    }}
                  />
                ))}
                {pendingVideoAsGenerating.map((item) => (
                  <GeneratingPlaceholderCard key={item.id} item={item} isVideo />
                ))}
                {/* Phase 5.5: in-flight drafts del nuevo flow preview-then-pay.
                    Sobreviven refresh, navegación a otras partes de Fluxi, y
                    cierre del modal. Polling cada 10s desde useVideoInFlightStore.
                    Cuando el draft llega a un estado terminal (FINISHED, ERROR,
                    REJECTED) desaparece del listado y el video final aparece
                    via la galería normal de creatives. */}
                {draftVideoPlaceholderCards.map((item) => (
                  <GeneratingPlaceholderCard key={item.id} item={item} isVideo />
                ))}
                {visibleReadyVideoItems.map((item, index) => (
                  <VideoPreviewCard key={item.id} item={item} onView={() => setViewVideoIndex(index)} />
                ))}
              </div>

              {filteredReadyVideoItems.length > 0 ? (
                <div ref={videoGallerySentinelRef} className="flex justify-center py-8">
                  {hasMoreReadyVideos ? (
                    <button
                      type="button"
                      onClick={loadMoreReadyVideos}
                      className="flex items-center gap-3 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-500 shadow-sm transition hover:border-gray-300 hover:text-gray-700"
                    >
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-fluxi" />
                      Cargar más videos
                    </button>
                  ) : (
                    <p className="text-xs text-gray-300">
                      {filteredReadyVideoItems.length} videos
                    </p>
                  )}
                </div>
              ) : null}
            </>
          )}

          <CreateVideoModal
            open={isCreateVideoModalOpen}
            onClose={() => {
              setIsCreateVideoModalOpen(false);
              // Phase 5.5: refresh inmediato al cerrar el modal para que la
              // gallery muestre la in-flight card del draft que el usuario
              // acaba de aprobar/rechazar/dejar a medias, sin esperar al
              // próximo tick de polling (10s).
              loadInFlightDrafts();
            }}
          />

          <ViewVideoModal
            videos={filteredReadyVideoItems}
            initialIndex={viewVideoIndex}
            open={viewVideoIndex >= 0}
            onClose={() => setViewVideoIndex(-1)}
            onVideoEdited={() => {
              void loadVideoGallery();
            }}
          />
        </div>
      )}

      {/* Spy tab */}
      {isSpyEnabled && activeTab === "spy" && (() => {
        const spyAdParam = searchParams.get("spy_ad");
        const initialDetailAdId = spyAdParam ? Number(spyAdParam) : null;
        return (
          <div className="w-full pb-8 ">
            <AdsSpyView
              onSwitchToImages={() => setActiveTab("images")}
              initialDetailAdId={initialDetailAdId}
              viewMode={spyViewMode}
              searchOpenSignal={spySearchOpenSignal}
            />
          </div>
        );
      })()}

      {/* Campaigns tab */}
      {isCampaignsEnabled && activeTab === "campaigns" && (
        <div className="w-full pb-8">
          <CampaignsView
            dateStart={campaignDateStart}
            dateEnd={campaignDateEnd}
            refreshKey={campaignRefreshKey}
            selectedAdAccount={campaignSelectedAccount}
          />
        </div>
      )}
    </div>
    </>
  );
}
